import { useFormik } from "formik";
import * as Yup from "yup";
import {
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Typography,
  Box,
  Breadcrumbs,
  Divider,
  Card,
  InputLabel,
  FormControl,
  Select,
  Stack,
  Checkbox,
  Autocomplete,
  IconButton,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Link from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ClearIcon from "@mui/icons-material/Clear";
import Layout from "../../layout/Layout";
import ImageUploader from "./ImageUpload";
import { Backdrop, CircularProgress } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { apiData } from "../../services/api_service";
import {
  stockTransferAPI,
  storesDropdownAPI,
  fetchStoreAPI,
  fetchProductsByStoreAPI,
} from "../../config";
import { AlertMessage } from "../../components/AlertMessage";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { LoadingButton } from "@mui/lab";
import { emirates } from "../../components/constants";

const validationSchema = Yup.object({
  product_id: Yup.number().required("Product is required"),
  from_store_id: Yup.number().required("From Store is required"),
  to_store_id: Yup.number()
    .required("To Store is required")
    .test(
      "different-store",
      "Stock and Transfer store should not be same",
      function (value) {
        return value !== this.parent.from_store_id;
      }
    ),
  stockAdjustment: Yup.number()
    .required("Quantity is required")
    .min(1, "Quantity must be at least 1"),
  reason: Yup.string().required("Reason is required"),
});

const StockTransfer = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const alertRef = useRef();

  const navigate = useNavigate();
  const [productOptions, setProductOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [storeOptions, setStoreOptions] = useState([]);
  const [fromStoreDetails, setFromStoreDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toStoreDetails, setToStoreDetails] = useState(null);
  const formik = useFormik({
    initialValues: {
      product_id: "",
      from_store_id: "",
      to_store_id: "",
      stockAdjustment: "",
      reason: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setLoading(true);
        const payload = {
          ...values,
          user_id: user.emp_id,
        };
        const result = await apiData("post", stockTransferAPI, payload);
        if (result.success) {
          alertRef.current.triggerAlert(
            JSON.stringify({
              message: result.msg,
              messageType: "success",
            })
          );
          setTimeout(() => {
            navigate("/reports/transaction-report");
          }, 2000);
        } else {
          alertRef.current.triggerAlert(
            JSON.stringify({
              message: result.msg,
              messageType: "error",
            })
          );
        }
      } catch (error) {
        alertRef.current.triggerAlert(
          JSON.stringify({
            message: "Something went wrong. Please try again.",
            messageType: "error",
          })
        );
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  const fetchStores = async () => {
    try {
      const result = await apiData("get", storesDropdownAPI);
      if (result.success) {
        setStoreOptions(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch stores:", error);
    }
  };

  const getStoreDetails = async (id, type) => {
    if (!id) return;

    try {
      const result = await apiData("get", `${fetchStoreAPI}?id=${id}`);
      if (result.success) {
        const store = result.data[0] || null; // ✅ take the first item
        if (type === "from") {
          setFromStoreDetails(store);
        } else {
          setToStoreDetails(store);
        }
      }
    } catch (error) {
      console.error("Error fetching store details:", error);
    }
  };

  const fetchProducts = async (storeId, search = "") => {
    if (!storeId) return;
    setLoadingProducts(true);
    try {
      const result = await apiData(
        "get",
        `${fetchProductsByStoreAPI}?id=${storeId}&search=${search}&includeExpired=1`
      );

      if (result.success) {
        setProductOptions(result.data);
      } else {
        setProductOptions([]);
      }
    } catch (error) {
      console.error("Failed to fetch products:", error);
      setProductOptions([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    fetchStores();
  }, []);

  const getNameById = (list, id) => {
    const found = list.find((item) => item.id === Number(id));
    return found ? found.name : id; // fallback to id if not found
  };

  return (
    <Layout>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Grid className="employee-form bold-header">
        <AlertMessage ref={alertRef} />
        <Grid container spacing={2} alignItems="center">
          {/* LEFT SIDE */}
          <Grid item xs={12} md={6} lg={6}>
            <Typography className="page_title" textAlign="left">
              Stock Transfer
            </Typography>

            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Stock Transfer</Typography>
            </Breadcrumbs>
          </Grid>

          {/* RIGHT SIDE – Buttons aligned like reference code */}
          <Grid item xs={12} md={6} lg={6}>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                justifyContent: { xs: "flex-start", md: "flex-end" },
                flexWrap: { xs: "wrap", md: "wrap" },
                alignItems: "center",
              }}
            >
              <LoadingButton
                size="medium"
                variant="contained"
                color="success"
                className="button_first"
                href="/reports/transaction-report"
                sx={{
                  fontSize: { xs: "0.7rem", md: "0.75rem", lg: "0.8rem" },
                  padding: { xs: "8px 12px", md: "8px 14px", lg: "10px 16px" },
                  lineHeight: 1.2,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                }}
              >
                STOCK TRANSACTION REPORT
              </LoadingButton>

              <LoadingButton
                size="medium"
                variant="contained"
                className="button_first"
                href="/inventory/bulk-upload"
                sx={{
                  fontSize: { xs: "0.7rem", md: "0.75rem", lg: "0.8rem" },
                  padding: { xs: "8px 12px", md: "8px 14px", lg: "10px 16px" },
                  lineHeight: 1.2,
                  textTransform: "none",
                  backgroundColor: "#7B1FA2",
                  whiteSpace: "nowrap",
                  "&:hover": { backgroundColor: "#6A1B9A" },
                }}
              >
                BULK UPLOAD
              </LoadingButton>

              <LoadingButton
                size="medium"
                variant="contained"
                color="primary"
                onClick={() => navigate(-1)}
                startIcon={<ArrowBackIosIcon />}
                sx={{
                  fontSize: { xs: "0.7rem", md: "0.75rem", lg: "0.85rem" },
                  padding: { xs: "5px 12px", md: "6px 12px", lg: "10px 16px" },
                  lineHeight: 1.2,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                }}
              >
                BACK
              </LoadingButton>
            </Box>
          </Grid>
        </Grid>

        <form className="form_design" onSubmit={formik.handleSubmit}>
          {/* <h3>Store Stock Transactions</h3> */}
          <Grid container spacing={3} mt={1}>
            <Grid item xs={12} lg={6} md={6}>
              <Autocomplete
                options={storeOptions}
                getOptionLabel={(option) => option.store_name || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={
                  storeOptions.find(
                    (s) => s.id === formik.values.from_store_id
                  ) || null
                }
                onChange={(e, newValue) => {
                  const storeId = newValue ? newValue.id : "";

                  formik.setFieldValue(
                    "from_store_id", storeId                    
                  );

                  if (storeId) {
                    getStoreDetails(storeId, "from");
                    fetchProducts(storeId); 
                  } else {
                    setFromStoreDetails(null);
                    setProductOptions([]);
                  }
                }}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    {option.store_name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select a Source Store"
                    fullWidth
                    error={
                      formik.touched.from_store_id &&
                      Boolean(formik.errors.from_store_id)
                    }
                    helperText={
                      formik.touched.from_store_id &&
                      formik.errors.from_store_id
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: <>{params.InputProps.endAdornment}</>,
                    }}
                  />
                )}
                ListboxProps={{
                  style: { maxHeight: 200, overflowY: "auto" },
                }}
              />
              {fromStoreDetails && (
                <Card sx={{ p: 2, mt: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {fromStoreDetails.store_name}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {fromStoreDetails.store_type_name}
                  </Typography>
                  <Typography>{fromStoreDetails.contact_person}</Typography>
                  <Typography>{fromStoreDetails.phone}</Typography>
                  <Typography>{fromStoreDetails.email}</Typography>
                  <Typography>
                    {fromStoreDetails.city},{" "}
                    {getNameById(emirates, fromStoreDetails.emirate)}
                  </Typography>
                </Card>
              )}
            </Grid>

            <Grid item xs={12} lg={6} md={6}>
              <Autocomplete
                options={storeOptions}
                getOptionLabel={(option) => option.store_name || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={
                  storeOptions.find(
                    (s) => s.id === formik.values.to_store_id
                  ) || null
                }
                onChange={(e, newValue) => {
                  const toStoreId = newValue ? newValue.id : "";
                  
                  formik.setFieldValue(
                    "to_store_id", toStoreId                    
                  );

                  if (toStoreId) {
                    getStoreDetails(toStoreId, "to");
                  } else {
                    setToStoreDetails(null);
                  }
                }}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    {option.store_name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select a Destination Store"
                    fullWidth
                    error={
                      formik.touched.to_store_id &&
                      Boolean(formik.errors.to_store_id)
                    }
                    helperText={
                      formik.touched.to_store_id && formik.errors.to_store_id
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: <>{params.InputProps.endAdornment}</>,
                    }}
                  />
                )}
                ListboxProps={{
                  style: { maxHeight: 200, overflowY: "auto" },
                }}
              />
              {toStoreDetails && (
                <Card sx={{ p: 2, mt: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {toStoreDetails.store_name}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {toStoreDetails.store_type_name}
                  </Typography>
                  <Typography>{toStoreDetails.contact_person}</Typography>
                  <Typography>{toStoreDetails.phone}</Typography>
                  <Typography>{toStoreDetails.email}</Typography>
                  <Typography>
                    {toStoreDetails.city},{" "}
                    {getNameById(emirates, toStoreDetails.emirate)}
                  </Typography>
                </Card>
              )}
            </Grid>

            <Grid item xs={12} lg={6} md={6}>
              <Autocomplete
                options={productOptions}
                // Products with only expired stock are listed (so the reason is clear) but can't be moved.
                getOptionDisabled={(option) => !(Number(option.quantity) > 0)}
                getOptionLabel={(option) =>
                  option.product_name
                    ? `${option.product_name} ${option.sku || ""}`
                    : ""
                }
                loading={loadingProducts}
                value={
                  productOptions.find(
                    (p) => p.product_id === formik.values.product_id
                  ) || null
                }
                onInputChange={(e, value, reason) => {
                  setSearchTerm(value);
                  if (reason === "input") {
                    fetchProducts(formik.values.from_store_id, value);
                  } else if (reason === "clear" || reason === "reset") {
                    fetchProducts(formik.values.from_store_id, "");
                  }
                }}
                onChange={(e, newValue) => {
                  formik.setFieldValue(
                    "product_id",
                    newValue ? newValue.product_id : ""
                  );
                }}
                // ⭐ HIGHLIGHT SELECTED OPTION
                renderOption={(props, option, { selected }) => {
                  const size = option.size || "NA";
                  const color = option.color || "NA";

                  return (
                    <li
                      {...props}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 12px",
                        borderBottom: "1px solid #f0f0f0",
                        cursor: "pointer",
                        backgroundColor: selected ? "#e3f2fd" : "white", // 🔵 selected highlight
                        gap: "12px",
                      }}
                    >
                      {option.product_image && (
                        <img
                          src={option.product_image}
                          alt={option.product_name}
                          style={{
                            width: 40,
                            height: 40,
                            objectFit: "contain",
                            borderRadius: 6,
                          }}
                        />
                      )}

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          flexGrow: 1,
                        }}
                      >
                        <span>
                          {option.product_name}{" "}
                          <span style={{ fontWeight: 400 }}>
                            (SKU: {option.sku})
                          </span>
                        </span>
                        <span style={{ fontSize: "0.85rem", color: "#007bff" }}>
                          Size: {size}, Colour: {color}
                        </span>
                      </div>

                      <span
                        style={{
                          color: option.quantity > 0 ? "green" : "red",
                          fontWeight: 500,
                          fontSize: "0.85rem",
                        }}
                      >
                        {option.quantity > 0
                          ? `In Stock: ${option.quantity}${Number(option.expired) > 0 ? ` · ${option.expired} expired` : ""}`
                          : Number(option.expired) > 0
                          ? `Expired: ${option.expired} (not transferable)`
                          : "Out of Stock"}
                      </span>
                    </li>
                  );
                }}
                // ⭐ SHOW SELECTED PRODUCT WITH IMAGE & DETAILS IN INPUT
                renderInput={(params) => {
                  const selected = productOptions.find(
                    (p) => p.product_id === formik.values.product_id
                  );

                  return (
                    <TextField
                      {...params}
                      label="Select a Product"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: selected ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              paddingLeft: 6,
                            }}
                          >
                            <img
                              src={selected.product_image}
                              alt={selected.product_name}
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: 4,
                                objectFit: "cover",
                                border: "1px solid #ccc",
                              }}
                            />

                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <span>
                                {selected.product_name} (SKU: {selected.sku})
                              </span>
                              <span style={{ fontSize: 11, color: "#1976d2" }}>
                                Size: {selected.size || "NA"}, Colour:{" "}
                                {selected.color || "NA"}
                              </span>
                            </div>
                          </div>
                        ) : null,

                        // hides duplicate text in input when selected
                        inputProps: {
                          ...params.inputProps,
                          value: selected ? "" : params.inputProps.value,
                        },
                      }}
                    />
                  );
                }}
                ListboxProps={{
                  style: { maxHeight: 200, overflowY: "auto" },
                }}
              />
            </Grid>

            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="stockAdjustment"
                type="number"
                label="Quantity"
                fullWidth
                value={formik.values.stockAdjustment}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.stockAdjustment &&
                  Boolean(formik.errors.stockAdjustment)
                }
                helperText={
                  formik.touched.stockAdjustment &&
                  formik.errors.stockAdjustment
                }
              />
            </Grid>

            <Grid item xs={12} lg={6} md={6}>
              <TextField
                name="reason"
                label="Reason / Description"
                fullWidth
                multiline
                rows={3}
                value={formik.values.reason}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.reason && Boolean(formik.errors.reason)}
                helperText={formik.touched.reason && formik.errors.reason}
              />
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="center" mt={2} mb={2} pr={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  Transfer
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Layout>
  );
};

export default StockTransfer;
